import { useCallback, useEffect, useRef, useState } from "react";

import { config, useSpring } from "@react-spring/web";
import { first, last } from "lodash/fp";

import { waitForScrollEnd } from "@app/analytics/utils";

import type { Direction, ScrollSliderProps, UseScrollSlider } from "./types";

const DEFAULT_OPTS = {
  scrollOffset: 6,
};

interface UseScrollSliderOpts {
  onWheel?: (opts: {
    canScrollBackward: boolean;
    canScrollForward: boolean;
  }) => void;
  /** The pixel distance from the beginning of the child element to target for each scroll */
  scrollOffset?: number;
  selected?: ScrollSliderProps["selected"];
}

const useScrollSlider = (
  opts: UseScrollSliderOpts = DEFAULT_OPTS
): UseScrollSlider => {
  const [scrollTo, setScrollTo] = useState({ from: 0, to: 0 });
  const [animating, setAnimating] = useState(false);
  const [wheeling, setWheeling] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [atScrollStart, setAtScrollStart] = useState(true);
  const [atScrollEnd, setAtScrollEnd] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const { scrollOffset = DEFAULT_OPTS.scrollOffset, selected } = opts;

  const updateScrollPosition = useCallback(
    (fromWheel = false) => {
      if (!listRef.current || !scrollerRef.current) return;
      const { right: scrollerRight, left: scrollerLeft } =
        scrollerRef.current.getBoundingClientRect();

      // splice to remove spacing shim
      const childList = Array.from(listRef.current.children).splice(
        0,
        listRef.current.children.length - 1
      );

      const isAtScrollStart =
        (first(childList)?.getBoundingClientRect().left ?? 0) >= scrollerLeft;
      const isAtScrollEnd =
        (last(childList)?.getBoundingClientRect().right ?? 0) <= scrollerRight;

      setAtScrollStart(isAtScrollStart);
      setAtScrollEnd(isAtScrollEnd);

      if (fromWheel) {
        opts.onWheel?.({
          canScrollBackward: !isAtScrollStart,
          canScrollForward: !isAtScrollEnd,
        });
      }
    },
    [listRef.current, scrollerRef.current, opts.onWheel]
  );

  const { scroll } = useSpring({
    from: { scroll: scrollTo.from },
    // if a user manually scrolled between the last animation, we want
    // to start the animation from scrollTo.from
    reset: !animating,
    // cancel animation if user is scrolling with mouse or trackpad
    cancel: wheeling,
    to: { scroll: scrollTo.to },
    config: config.slow,
    onRest: () => {
      // prevent the animation from starting over
      setScrollTo({ from: scrollTo.to, to: scrollTo.to });
      setAnimating(false);
      updateScrollPosition();
    },
    onStart: () => setAnimating(true),
  });

  const handleScrollClick = (direction: Direction) => {
    if (!scrollerRef.current || !listRef.current) return;
    setWheeling(false);

    const left = scrollerRef.current?.getBoundingClientRect().left;
    const scrollLeft = scrollerRef.current?.scrollLeft;

    // splice to remove spacing shim
    let childList = Array.from(listRef.current.children).splice(
      0,
      listRef.current.children.length - 1
    );
    if (direction === "backward") {
      childList = childList.reverse();
    }

    // if animating we want the next child after scrollTo.to,
    // otherwise we want the next child after scrollLeft
    const targetScrollLeft = animating ? scrollTo.to : scrollLeft;

    let childScrollLeft = null;
    const nextChild = childList.find((child) => {
      const absChildLeft = child.getBoundingClientRect().left;
      // get child left relative to parent
      const childLeft = absChildLeft - left;
      // get child left relative to scroll with some padding
      childScrollLeft = scrollLeft + childLeft - scrollOffset;

      return (
        (direction === "backward" && childScrollLeft < targetScrollLeft) ||
        (direction === "forward" && childScrollLeft > targetScrollLeft)
      );
    });

    if (nextChild && childScrollLeft) {
      setScrollTo({ from: scrollLeft, to: childScrollLeft });
    }
  };

  useEffect(() => {
    if (
      !autoScroll ||
      !scrollerRef.current ||
      !listRef.current ||
      selected == null
    ) {
      return;
    }

    const left = scrollerRef.current.getBoundingClientRect().left;
    const scrollLeft = scrollerRef.current.scrollLeft;

    const child = Array.from(listRef.current.children)[selected];

    if (child) {
      const absChildLeft = child.getBoundingClientRect().left;
      const childLeft = absChildLeft - left;
      const childScrollLeft = scrollLeft + childLeft - scrollOffset;
      setScrollTo({ from: scrollLeft, to: childScrollLeft });

      // only auto scroll if the component was mounted with a pre-selected item;
      // from that point forward scroll should be dictated by the user
      setAutoScroll(false);
    }
  }, [!!scrollerRef.current, !!listRef.current, selected, autoScroll]);

  useEffect(() => {
    // set initial scroll state
    updateScrollPosition();
  }, [updateScrollPosition]);

  const handleWheel = useCallback(() => {
    setWheeling(true);
    waitForScrollEnd(scrollerRef.current).then(() => {
      setWheeling(false);
      updateScrollPosition(true);
    });
  }, [updateScrollPosition]);

  return {
    canScrollBackward: !atScrollStart,
    canScrollForward: !atScrollEnd,
    listRef,
    onScrollClick: handleScrollClick,
    onWheel: handleWheel,
    scroll,
    scrollerRef,
  };
};

export default useScrollSlider;
